import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { createForm } from 'rc-form';
import { Typography, Grid, Button, Card, CardContent } from '@material-ui/core/';

import { selectUser } from 'yeself-client-common/security/SecuritySelector';

import DrawerWrapper from 'Console/components/common/drawer/DrawerWrapper';
import Loading from 'Console/components/common/loading/Loading';
import RegisterUser from 'Console/components/accounts/RegisterUser';
import ConnectedTable from 'Console/components/common/connectedTable/ConnectedTable';

import { fetchAccounts, registerUser, registerUserError } from 'Console/data/accounts/actions';
import { pushNotificationToStack } from 'Console/data/notification/actions';
import {
    selectAccounts,
    selectAccountsHasError,
    selectAccountsIsLoading,
} from 'Console/data/accounts/selectors';
import { ACCOUNT_DETAIL_ROUTE } from 'Console/constants/routes';

const isFormValid = (form) => {
    // if there is no error for given field error is undefined
    const errors = Object.values(form.getFieldsError()).filter((error) => error);
    return errors.length === 0;
};

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: 24,
    },
    tableRow: {
        cursor: 'pointer',
    },
});

class Accounts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDrawerWrapperShown: false,
        };
    }

    componentDidMount() {
        const { fetchAccounts, user } = this.props;
        fetchAccounts(user.groups);
    }

    handleRegistrationUser = async () => {
        const {
            form: { getFieldsValue, validateFields },
            registerUser,
        } = this.props;

        const errors = await validateFields();

        if (errors.length > 0) {
            return;
        }

        const formValues = getFieldsValue([
            'registerUserEmail',
            'registerUserFirstName',
            'registerUserLastName',
            'registerUserPassword',
        ]);

        const userData = {
            email: formValues.registerUserEmail,
            firstName: formValues.registerUserFirstName,
            lastName: formValues.registerUserLastName,
            password: formValues.registerUserPassword,
        };

        registerUser(userData, {
            success: (
                <FormattedMessage
                    id="Notification.register.user.success"
                    defaultMessage="User registration has been successful"
                />
            ),
            fail: (
                <FormattedMessage
                    id="Notification.register.user.error"
                    defaultMessage="User registration hasn't been successful"
                />
            ),
        });
        this.closeDrawerWrapper();
    };

    onRowClick = (account) => {
        const { push } = this.props;
        push(`${ACCOUNT_DETAIL_ROUTE}${account.id}`);
    };

    openDrawerWrapper = () => this.toggleDrawer(true);

    closeDrawerWrapper = () => this.toggleDrawer(false);

    toggleDrawer = (value) => {
        this.setState({ isDrawerWrapperShown: value });
    };

    render() {
        const { classes, accounts, isLoading, hasError, form } = this.props;
        const { isDrawerWrapperShown } = this.state;

        if (isLoading || !accounts) {
            return <Loading minHeight="300" />;
        }

        if (hasError) {
            pushNotificationToStack(
                <FormattedMessage
                    id="Notification.fail.loadData"
                    defaultMessage="Unable to load data, something went wrong"
                />
            );
            return null;
        }

        const labels = [
            { fieldName: 'id', ignoreRender: true },
            {
                fieldName: 'userName',
            },
            {
                fieldName: 'fistName',
            },

            {
                fieldName: 'lastName',
            },
            {
                fieldName: 'email',
            },
            {
                fieldName: 'verified',
            },
        ];

        return (
            <Grid container className={classes.root}>
                <Grid>
                    <DrawerWrapper
                        title={
                            <FormattedMessage
                                id="Accounts.register.user"
                                defaultMessage="User registration"
                            />
                        }
                        name={<FormattedMessage id="Buttons.cancel" defaultMessage="Cancel" />}
                        term={<FormattedMessage id="Buttons.create" defaultMessage="Create" />}
                        isShown={isDrawerWrapperShown}
                        onClose={this.closeDrawerWrapper}
                        onConfirm={this.handleRegistrationUser}
                        confirmDisabled={!isFormValid(form)}
                    >
                        <RegisterUser form={form} />
                    </DrawerWrapper>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={24} justify="center" alignItems="flex-end">
                        <Grid item xs={12} md={10}>
                            <Grid
                                style={{ marginBottom: '28px' }}
                                container
                                justify="space-between"
                                alignItems="flex-end"
                            >
                                <Grid item>
                                    <Typography variant="h6">
                                        <FormattedMessage
                                            id="Accounts.title"
                                            defaultMessage="Accounts"
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography>
                                        <Button
                                            id="yeself-button"
                                            variant="contained"
                                            color="primary"
                                            onClick={this.openDrawerWrapper}
                                        >
                                            <FormattedMessage
                                                id="Buttons.registerUser"
                                                defaultMessage="Register user"
                                            />
                                        </Button>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={10}>
                            <Card className={classes.card}>
                                <CardContent>
                                    <ConnectedTable
                                        labels={labels}
                                        rows={accounts.map(
                                            ({
                                                id,
                                                username,
                                                firstName,
                                                lastName,
                                                email,
                                                emailVerified,
                                            }) => ({
                                                id,
                                                username,
                                                firstName,
                                                lastName,
                                                email,
                                                emailVerified,
                                            })
                                        )}
                                        translationKey="Tables.accounts"
                                        onRowClick={this.onRowClick}
                                        className={classes.tableRow}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

export default connect(
    (state) => ({
        accounts: selectAccounts(state),
        isLoading: selectAccountsIsLoading(state),
        hasError: selectAccountsHasError(state),
        user: selectUser(state),
    }),
    {
        fetchAccounts,
        push,
        registerUser,
        registerUserError,
        pushNotificationToStack,
    }
)(withStyles(styles)(createForm()(Accounts)));
